import React from "react"
import {
    View,
    StyleSheet,
    Text,
    ActivityIndicator,
    Image,
    TouchableOpacity,
} from "react-native"
import { FontAwesome5 } from "@expo/vector-icons"
import moment from "moment"
import "moment/locale/fr"
import { theme } from "@common/theme/theme"
import { subjectIconUri, capitalize } from "../constants/summerContent"
import FunnelFadeUp from "@common/components/funnel/FunnelFadeUp"

/**
 * Calendrier du programme d'été personnalisé — source DRY partagée entre
 * l'étape "programme" (SummerPlanScreen) et la modale d'aperçu du checkout
 * (SummerSlotsCheckoutScreen).
 *
 * Rend : le sous-titre "{hoursPerWeek} séances/semaine…", la légende des
 * matières (pills, tappables quand `onEditHours` est fourni → règle les heures
 * par matière), le calendrier hebdomadaire séance par séance (date + horaire →
 * matière → chapitre + thème, avec la classe affichée pour les chapitres de prise
 * d'avance), le fallback "programme détaillé arrive" et le bandeau réassurance.
 *
 * Gère son propre ActivityIndicator quand `loading`.
 *
 * @param {function} [onEditHours]  Quand fourni, les chips de la légende deviennent
 *                                  tappables (option power user : modale de
 *                                  répartition heures/matière). Omis sur la modale
 *                                  d'aperçu read-only du checkout.
 * @param {"weekly"|"total"} [subjectHoursMode]  Sémantique des heures des chips
 *                                  matières. "weekly" (défaut, été) : le payload
 *                                  porte des heures hebdo → « 2h/sem ». "total"
 *                                  (stage) : le payload porte le TOTAL planifié
 *                                  sur tout le programme → « 15h », sans mention
 *                                  hebdo trompeuse.
 * @param {boolean} [showParts]     Affiche « · 1/2 » / « · 2/2 » à côté du
 *                                  chapitre quand la séance porte un `part`
 *                                  (stage ≥ 4ème : un chapitre couvre les 2
 *                                  créneaux du jour). Off par défaut (été
 *                                  inchangé).
 * @param {boolean} [hideSubtitle]  Masque le sous-titre « X séances/semaine… »
 *                                  (stage : le rythme est déjà porté par les
 *                                  sessions choisies — la ligne n'apporte rien).
 *                                  Off par défaut (été inchangé).
 *
 * Le label « Semaine N » n'est rendu que si le programme couvre PLUS d'une
 * semaine (inutile de titrer « Semaine 1 » quand il n'y en a qu'une — cas d'une
 * session stage unique ; l'été est toujours multi-semaines, donc inchangé).
 */

moment.locale("fr")

const formatSessionDate = (date) =>
    capitalize(moment(date).locale("fr").format("ddd D MMM"))

// "10:30" → "10h30" ; "18:30" → "18h30" (affichage horaire compact fr).
const formatSessionTime = (time) =>
    typeof time === "string" ? time.replace(":", "h") : null

const SummerProgramCalendar = ({
    program,
    firstname,
    loading,
    failed,
    onEditHours,
    subjectHoursMode = "weekly",
    showParts = false,
    hideSubtitle = false,
    // Le stage, quand le parent vient d'adapter son programme : les séances
    // reviennent une à une, et celles dont le chapitre a changé s'allument.
    enterAnimation = false,
    highlightChapterIds = null,
}) => {
    const childName = firstname || "votre enfant"

    if (loading) {
        return (
            <View style={styles.loadingBlock}>
                <ActivityIndicator color={theme.colors.primary} />
                <Text style={styles.loadingText}>
                    On compose le programme de {childName}…
                </Text>
            </View>
        )
    }

    const programSubjects = program?.subjects || []
    const schedule = program?.schedule || []
    const hoursPerWeek = program?.hours_per_week || 3
    const hasSchedule = schedule.length > 0

    return (
        <>
            {hideSubtitle ? null : (
                <Text style={styles.subtitle}>
                    {hoursPerWeek} séance{hoursPerWeek > 1 ? "s" : ""}/semaine en groupe, adaptées au niveau de {childName}.
                </Text>
            )}

            {programSubjects.length > 0 ? (
                <>
                    <View style={styles.legend}>
                        {programSubjects.map((subject) => {
                            const editable = !!onEditHours && hasSchedule
                            const PillTag = editable ? TouchableOpacity : View
                            return (
                                <PillTag
                                    key={String(subject.id)}
                                    style={[
                                        styles.legendPill,
                                        editable && styles.legendPillEditable,
                                    ]}
                                    {...(editable
                                        ? {
                                              activeOpacity: 0.7,
                                              onPress: onEditHours,
                                              accessibilityRole: "button",
                                              accessibilityLabel: `Modifier les heures par matière (${subject.name})`,
                                          }
                                        : {})}
                                >
                                    {subjectIconUri(subject.icon_path) ? (
                                        <Image
                                            source={{
                                                uri: subjectIconUri(
                                                    subject.icon_path
                                                ),
                                            }}
                                            style={styles.legendIcon}
                                            resizeMode="contain"
                                        />
                                    ) : null}
                                    <Text style={styles.legendName}>
                                        {subject.name}
                                    </Text>
                                    {subject.weekly_hours ? (
                                        <Text style={styles.legendHours}>
                                            {" "}· {subject.weekly_hours}h
                                            {subjectHoursMode === "weekly"
                                                ? "/sem"
                                                : ""}
                                        </Text>
                                    ) : null}
                                    {editable ? (
                                        <FontAwesome5
                                            name="pen"
                                            solid
                                            size={9}
                                            color={theme.colors.primary}
                                            style={styles.legendEditIcon}
                                        />
                                    ) : null}
                                </PillTag>
                            )
                        })}
                    </View>
                    {!!onEditHours && hasSchedule ? (
                        <Text style={styles.legendEditHint}>
                            Touchez une matière pour ajuster les heures.
                        </Text>
                    ) : null}
                </>
            ) : null}

            {failed || !hasSchedule ? (
                <Text style={styles.illustrativeNote}>
                    Le programme détaillé arrive — votre tuteur le personnalise dès la 1ʳᵉ séance.
                </Text>
            ) : (
                schedule.map((week) => (
                    <View key={String(week.week_index)} style={styles.weekBlock}>
                        {schedule.length > 1 ? (
                            <Text style={styles.weekLabel}>Semaine {week.week_index}</Text>
                        ) : null}
                        {(week.sessions || []).map((session, idx) => {
                            const Card = enterAnimation ? FunnelFadeUp : View
                            const changed = !!highlightChapterIds?.has(
                                session.chapter?.id
                            )
                            // La classe d'un chapitre hors de celle du
                            // programme : la suivante (été), ou la précédente
                            // d'un stage pendant l'année (« rappel de 4ème »).
                            const classTag = !session.chapter?.class
                                ? null
                                : session.chapter.is_previous_class
                                  ? `rappel de ${session.chapter.class}`
                                  : session.chapter.is_rentree
                                    ? session.chapter.class
                                    : null
                            return (
                                <Card
                                    key={`${week.week_index}-${idx}`}
                                    index={idx}
                                    style={[
                                        styles.sessionCard,
                                        changed && styles.sessionCardChanged,
                                    ]}
                                >
                                    <View style={styles.sessionDateRow}>
                                        <Text style={styles.sessionDate}>
                                            {formatSessionDate(session.date)}
                                            {formatSessionTime(session.time)
                                                ? ` · ${formatSessionTime(session.time)}`
                                                : ""}
                                        </Text>
                                    </View>
                                    <View style={styles.sessionBody}>
                                        {subjectIconUri(session.subject?.icon_path) ? (
                                            <Image
                                                source={{
                                                    uri: subjectIconUri(
                                                        session.subject?.icon_path
                                                    ),
                                                }}
                                                style={styles.sessionIcon}
                                                resizeMode="contain"
                                            />
                                        ) : null}
                                        <View style={styles.sessionTexts}>
                                            <Text
                                                style={styles.sessionChapter}
                                                numberOfLines={2}
                                            >
                                                {session.chapter?.name}
                                                {showParts &&
                                                session.part?.index &&
                                                session.part?.total ? (
                                                    <Text
                                                        style={
                                                            styles.sessionPart
                                                        }
                                                    >
                                                        {" "}· {
                                                            session.part.index
                                                        }/{session.part.total}
                                                    </Text>
                                                ) : null}
                                            </Text>
                                            {session.chapter?.theme ||
                                            classTag ? (
                                                <Text
                                                    style={styles.sessionTheme}
                                                    numberOfLines={1}
                                                >
                                                    {session.chapter?.theme}
                                                    {classTag ? (
                                                        <Text style={styles.sessionClass}>
                                                            {session.chapter?.theme
                                                                ? " · "
                                                                : ""}
                                                            {classTag}
                                                        </Text>
                                                    ) : null}
                                                </Text>
                                            ) : null}
                                        </View>
                                    </View>
                                    {changed ? (
                                        <View style={styles.changedTag}>
                                            <FontAwesome5
                                                name="check"
                                                size={9}
                                                color={theme.colors.successDark}
                                            />
                                            <Text style={styles.changedTagText}>
                                                selon votre demande
                                            </Text>
                                        </View>
                                    ) : null}
                                </Card>
                            )
                        })}
                    </View>
                ))
            )}
        </>
    )
}

const styles = StyleSheet.create({
    subtitle: { fontFamily: "Montserrat-Regular", fontSize: 13, color: theme.colors.grey, marginBottom: 14 },
    illustrativeNote: { fontFamily: "Montserrat-Regular", fontSize: 13, color: theme.colors.grey, marginBottom: 16 },
    loadingBlock: { alignItems: "center", paddingVertical: 48, gap: 14 },
    loadingText: { fontFamily: "Montserrat-Regular", fontSize: 14, color: theme.colors.grey, textAlign: "center" },

    // Légende matières (pills compactes).
    legend: { flexDirection: "row", flexWrap: "wrap", marginBottom: 6 },
    legendPill: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 999,
        paddingVertical: 4,
        paddingHorizontal: 10,
        marginRight: 8,
        marginBottom: 8,
    },
    legendPillEditable: {
        borderWidth: 1,
        borderColor: theme.colors.primary,
    },
    legendIcon: { width: 16, height: 16, marginRight: 6 },
    legendName: { fontFamily: "Montserrat-Bold", fontSize: 11, color: theme.colors.primary },
    legendHours: { fontFamily: "Montserrat-Regular", fontSize: 11, color: theme.colors.primary },
    legendEditIcon: { marginLeft: 6 },
    legendEditHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        marginBottom: 16,
    },

    // Calendrier hebdomadaire.
    weekBlock: { marginBottom: 18 },
    weekLabel: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 15,
        color: theme.colors.primary,
        marginBottom: 8,
    },
    sessionCard: {
        backgroundColor: theme.colors.whiteAlmost,
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginBottom: 8,
    },
    sessionCardChanged: {
        backgroundColor: theme.colors.successLight,
        borderWidth: 1.5,
        borderColor: theme.colors.successGray,
    },
    changedTag: {
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "flex-start",
        gap: 4,
        marginTop: 6,
        paddingVertical: 2,
        paddingHorizontal: 8,
        borderRadius: 999,
        backgroundColor: theme.colors.white,
    },
    changedTagText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 10.5,
        color: theme.colors.successDark,
    },
    sessionDateRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 6,
    },
    sessionDate: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.warning || "#C2410C",
    },
    sessionBody: { flexDirection: "row", alignItems: "center" },
    sessionIcon: { width: 22, height: 22, marginRight: 10 },
    sessionTexts: { flex: 1 },
    sessionChapter: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.regular || theme.colors.dark,
    },
    sessionTheme: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        marginTop: 2,
    },
    // « · 1/2 » à côté du chapitre quand il couvre les 2 créneaux du jour
    // (stage ≥ 4ème) — discret, même corps que le nom du chapitre.
    sessionPart: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
    },
    // Classe affichée à côté du thème quand le chapitre est du niveau de la
    // rentrée (prise d'avance) — remplace l'ancien badge "Avance".
    sessionClass: { fontFamily: "Montserrat-Bold", color: theme.colors.success || "#00BE89" },
})

export default SummerProgramCalendar
